// Multilingual share widget — WhatsApp / Email / Facebook / Copy link with pre-filled message in current locale.
// Designed for Festas pages (Présence) to maximize word-of-mouth toward European communities.
import { useState } from "react";
import { useI18n } from "@/i18n/I18nContext";

// Pre-filled messages by locale. {url} is replaced at runtime with the current page URL.
const COPY = {
  fr: {
    label: "Partager cette page",
    helper: "Faites connaître les Fêtes de saint Léonard 2027",
    whatsapp: "Découvrez les Fêtes de saint Léonard — 2, 3 et 4 juillet 2027 au Prieuré de Vains (Baie du Mont-Saint-Michel). Trois jours de Libération, Lumière et Unité.",
    emailSubject: "Fêtes de saint Léonard — Millenium 2027",
    emailBody: "Bonjour,\n\nJe vous transmets cette page sur les Fêtes de saint Léonard, qui se tiendront les 2, 3 et 4 juillet 2027 au Prieuré de Vains, dans la baie du Mont-Saint-Michel. Trois jours de Libération, Lumière et Unité.\n\nÀ découvrir : {url}\n\nBien à vous,",
    copy: "Copier le lien",
    copied: "Lien copié ✓",
    whatsappBtn: "WhatsApp",
    emailBtn: "Email",
    facebookBtn: "Facebook",
  },
  en: {
    label: "Share this page",
    helper: "Spread the word about the Feasts of Saint Léonard 2027",
    whatsapp: "Discover the Feasts of Saint Léonard — 2, 3 and 4 July 2027 at the Priory of Vains (Mont-Saint-Michel Bay). Three days of Liberation, Light and Unity.",
    emailSubject: "Feasts of Saint Léonard — Millenium 2027",
    emailBody: "Hello,\n\nI'm sharing this page about the Feasts of Saint Léonard, which will take place on 2, 3 and 4 July 2027 at the Priory of Vains, in Mont-Saint-Michel Bay. Three days of Liberation, Light and Unity.\n\nLearn more: {url}\n\nKind regards,",
    copy: "Copy link",
    copied: "Link copied ✓",
    whatsappBtn: "WhatsApp",
    emailBtn: "Email",
    facebookBtn: "Facebook",
  },
  it: {
    label: "Condividi questa pagina",
    helper: "Fai conoscere le Feste di San Leonardo 2027",
    whatsapp: "Scoprite le Feste di San Leonardo — 2, 3 e 4 luglio 2027 al Priorato di Vains (Baia di Mont-Saint-Michel). Tre giorni di Liberazione, Luce e Unità.",
    emailSubject: "Feste di San Leonardo — Millenium 2027",
    emailBody: "Buongiorno,\n\nVi inoltro questa pagina sulle Feste di San Leonardo, che si terranno il 2, 3 e 4 luglio 2027 al Priorato di Vains, nella baia di Mont-Saint-Michel. Tre giorni di Liberazione, Luce e Unità.\n\nDa scoprire: {url}\n\nCordialmente,",
    copy: "Copia il link",
    copied: "Link copiato ✓",
    whatsappBtn: "WhatsApp",
    emailBtn: "Email",
    facebookBtn: "Facebook",
  },
  es: {
    label: "Compartir esta página",
    helper: "Difunda las Fiestas de San Leonardo 2027",
    whatsapp: "Descubran las Fiestas de San Leonardo — 2, 3 y 4 de julio de 2027 en el Priorato de Vains (Bahía del Mont-Saint-Michel). Tres días de Liberación, Luz y Unidad.",
    emailSubject: "Fiestas de San Leonardo — Millenium 2027",
    emailBody: "Hola,\n\nLes transmito esta página sobre las Fiestas de San Leonardo, que tendrán lugar los días 2, 3 y 4 de julio de 2027 en el Priorato de Vains, en la bahía del Mont-Saint-Michel. Tres días de Liberación, Luz y Unidad.\n\nA descubrir: {url}\n\nCordialmente,",
    copy: "Copiar el enlace",
    copied: "Enlace copiado ✓",
    whatsappBtn: "WhatsApp",
    emailBtn: "Correo",
    facebookBtn: "Facebook",
  },
  de: {
    label: "Diese Seite teilen",
    helper: "Machen Sie die Feste des heiligen Leonhard 2027 bekannt",
    whatsapp: "Entdecken Sie die Feste des heiligen Leonhard — 2., 3. und 4. Juli 2027 im Priorat von Vains (Bucht des Mont-Saint-Michel). Drei Tage Befreiung, Licht und Einheit.",
    emailSubject: "Feste des heiligen Leonhard — Millenium 2027",
    emailBody: "Guten Tag,\n\nIch leite Ihnen diese Seite über die Feste des heiligen Leonhard weiter, die vom 2. bis 4. Juli 2027 im Priorat von Vains in der Bucht des Mont-Saint-Michel stattfinden werden. Drei Tage der Befreiung, des Lichts und der Einheit.\n\nZu entdecken: {url}\n\nMit freundlichen Grüßen,",
    copy: "Link kopieren",
    copied: "Link kopiert ✓",
    whatsappBtn: "WhatsApp",
    emailBtn: "E-Mail",
    facebookBtn: "Facebook",
  },
  pt: {
    label: "Partilhar esta página",
    helper: "Dê a conhecer as Festas de São Leonardo 2027",
    whatsapp: "Descubram as Festas de São Leonardo — 2, 3 e 4 de julho de 2027 no Priorado de Vains (Baía do Monte Saint-Michel). Três dias de Libertação, Luz e Unidade.",
    emailSubject: "Festas de São Leonardo — Millenium 2027",
    emailBody: "Olá,\n\nTransmito-vos esta página sobre as Festas de São Leonardo, que terão lugar a 2, 3 e 4 de julho de 2027 no Priorado de Vains, na baía do Monte Saint-Michel. Três dias de Libertação, Luz e Unidade.\n\nA descobrir: {url}\n\nCom os melhores cumprimentos,",
    copy: "Copiar o link",
    copied: "Link copiado ✓",
    whatsappBtn: "WhatsApp",
    emailBtn: "E-mail",
    facebookBtn: "Facebook",
  },
};

// Brand-neutral line-art SVG icons
const Icon = {
  whatsapp: (p) => (<svg {...p} viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 21l1.7-5A8 8 0 1 1 8 19l-5 2z" strokeLinejoin="round" strokeLinecap="round"/><path d="M8.5 9.2c0 3.5 3.3 6.5 6.5 6.5l1.6-1.6-2-1-1 1c-1.6-.8-2.7-1.9-3.5-3.5l1-1-1-2-1.6 1.6z"/></svg>),
  email:    (p) => (<svg {...p} viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>),
  facebook: (p) => (<svg {...p} viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 7h3V4h-3a4 4 0 0 0-4 4v3H7v3h3v7h3v-7h3l1-3h-4V8a1 1 0 0 1 1-1z"/></svg>),
  copy:     (p) => (<svg {...p} viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="8" y="8" width="12" height="12" rx="2"/><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"/></svg>),
  check:    (p) => (<svg {...p} viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><path d="m5 12 5 5 9-11"/></svg>),
};

export default function ShareWidget({ compact = false }) {
  const { locale } = useI18n();
  const tr = COPY[locale] || COPY.fr;
  const [copied, setCopied] = useState(false);

  const url = typeof window !== "undefined" ? window.location.href : "";
  const encUrl = encodeURIComponent(url);
  const whatsappText = encodeURIComponent(`${tr.whatsapp}\n\n${url}`);
  const mailtoHref = `mailto:?subject=${encodeURIComponent(tr.emailSubject)}&body=${encodeURIComponent(tr.emailBody.replace("{url}", url))}`;

  const onCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      /* no-op */
    }
  };

  const btnStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
    padding: compact ? "0.45rem 0.7rem" : "0.6rem 0.95rem",
    background: "transparent",
    border: "1px solid var(--border)",
    color: "var(--ink-2)",
    fontFamily: "inherit",
    fontSize: compact ? "0.7rem" : "0.78rem",
    letterSpacing: "0.04em",
    cursor: "pointer",
    transition: "color .2s, border-color .2s, background .2s",
    textDecoration: "none",
  };

  const onHover = (e, on) => {
    e.currentTarget.style.color = on ? "var(--cinnabar)" : "var(--ink-2)";
    e.currentTarget.style.borderColor = on ? "var(--cinnabar)" : "var(--border)";
  };

  return (
    <section data-testid="share-widget" className="py-12" style={{ background: "var(--parchment-2)", borderTop: "1px solid var(--border)" }}>
      <div className="container-prieure text-center">
        <div className="font-caps text-[0.6rem] mb-2" style={{ color: "var(--gold)", letterSpacing: "0.28em" }}>
          ◇ {tr.label.toUpperCase()} ◇
        </div>
        <p className="font-display italic text-base mb-5" style={{ color: "var(--ink-2)" }}>{tr.helper}</p>
        <div className="flex flex-wrap justify-center gap-2.5">
          <a
            data-testid="share-whatsapp"
            href={`https://api.whatsapp.com/send?text=${whatsappText}`}
            target="_blank" rel="noreferrer noopener"
            style={btnStyle}
            onMouseEnter={(e) => onHover(e, true)} onMouseLeave={(e) => onHover(e, false)}
          >
            <Icon.whatsapp /> {tr.whatsappBtn}
          </a>
          <a
            data-testid="share-email"
            href={mailtoHref}
            style={btnStyle}
            onMouseEnter={(e) => onHover(e, true)} onMouseLeave={(e) => onHover(e, false)}
          >
            <Icon.email /> {tr.emailBtn}
          </a>
          <a
            data-testid="share-facebook"
            href={`https://www.facebook.com/sharer/sharer.php?u=${encUrl}`}
            target="_blank" rel="noreferrer noopener"
            style={btnStyle}
            onMouseEnter={(e) => onHover(e, true)} onMouseLeave={(e) => onHover(e, false)}
          >
            <Icon.facebook /> {tr.facebookBtn}
          </a>
          <button
            type="button"
            data-testid="share-copy"
            onClick={onCopy}
            style={{ ...btnStyle, color: copied ? "var(--moss)" : btnStyle.color, borderColor: copied ? "var(--moss)" : btnStyle.borderColor }}
            onMouseEnter={(e) => { if (!copied) onHover(e, true); }}
            onMouseLeave={(e) => { if (!copied) onHover(e, false); }}
          >
            {copied ? <Icon.check /> : <Icon.copy />}
            {copied ? tr.copied : tr.copy}
          </button>
        </div>
      </div>
    </section>
  );
}
